/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 */

import { Component, Inject, OnInit } from '@angular/core';
import { BULB_BUSY_COVER_SERVICE } from '../angularjs.providers';
import { PlanListInfo } from './plan-list-info.model';
import { PlanListInterval } from './plan-list-plan.enum';
import { PlanListService } from './plan-list.service';
import { FEATURE_CONSTANTS } from '../features-const';

/**
 * Component that houses the list of plans and checkout process to stripe.
 */
@Component({
  selector: 'bulb-plan-list',
  templateUrl: './plan-list.component.html',
  standalone: false,
})
export class PlanListComponent implements OnInit {
  /**
   * To show the accordion.
   */
  showText: boolean = false;

  /**
   * Contains element with bulb-view class.
   */
  bulbView: any;

  /**
   * Contains data of price list.
   */
  planListInfo: Array<PlanListInfo> = new Array<PlanListInfo>();

  /**
   * Contains price which is 12 times of monthly plan.
   */
  actualAnnualPrice: number;

  /**
   * Exposing plan interval enum so the template can use it.
   */
  public planInterval = PlanListInterval;

  /**
   * List of features to display.
   */
  public features = FEATURE_CONSTANTS;

  standardFeatures = [
    { key: 'planList.yoursLife', value: 'Yours for life' },
    { key: 'planList.uploadContent', value: 'Upload any type of content or file' },
    { key: 'planList.share.portfolio', value: 'Share your portfolio to anyone' },
    { key: 'planList.importContent', value: 'Import content from the web' },
    { key: 'planList.resourceLibrary', value: 'Access to the Resource Library' },
    { key: 'planList.collaborate', value: 'Collaborate' },
    { key: 'planList.integrateApps', value: 'Integrate with other apps' },
    { key: 'planList.bulbSupport', value: 'bulb support' },
  ];

  /**
   * Default Constructor.
   *
   * @param bulbBusyCoverService
   * @param bulbModalService
   * @param planlistService service for making api calls related to Plan list.
   */
  constructor(
    @Inject(BULB_BUSY_COVER_SERVICE) private bulbBusyCoverService,
    @Inject('BulbModalService') private bulbModalService,
    private planlistService : PlanListService,
  ) { }

  /**
   * A lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   */
  ngOnInit() {
    this.fetchPlanListData();
  }

  /**
   * Method to hide and show accordion.
   */
  toggleAccordionText() {
    this.showText = !this.showText;
  }

  /**
   * Method to get the price list for the products.
   */
  fetchPlanListData() {
    const hideBusy = this.bulbBusyCoverService.showBusy();
    this.planlistService.getPlanListData().subscribe((product) => {
      hideBusy();
      this.planListInfo = product.prices
        .sort((firstPlan, secondPlan) => firstPlan.interval.localeCompare(secondPlan.interval));
      this.actualAnnualPrice = (this.planListInfo
        .filter((prices) => prices.interval === PlanListInterval.MONTH)[0].price) * 12;
    }, () => {
      hideBusy();
      this.bulbModalService.show({
        titleCode: 'server_error_title',
        bodyCode: 'server_error_body',
        showCancelButton: false,
      });
    });
  }

  /**
   * Method to check out to stripe portal for billing.
   *
   * @param priceId contains the plan priceId.
   */
  selectPlan(priceId) {
    const hideBusy = this.bulbBusyCoverService.showBusy();
    this.planlistService.createCheckoutSession(priceId).subscribe((stripe) => {
      hideBusy();
      window.location.href = stripe.url;
    }, () => {
      hideBusy();
      this.bulbModalService.show({
        titleCode: 'server_error_title',
        bodyCode: 'server_error_body',
        showCancelButton: false,
      });
    });
  }

  /**
   * Method to return the discount percentage on annual plan.
   *
   * @param price contains the Annual plan price.
   */
  getDiscount(price) {
    return ((this.actualAnnualPrice / 100 - (price / 100)) * 100) / (this.actualAnnualPrice / 100);
  }
}
